import React from 'react';
import { SportsEnum } from 'types/generated/client';
import classNames from 'styles/utils/classNames';
import { SportBadgeProps } from './types';

const sportConfig: Record<
  SportsEnum,
  {
    label: string;
    iconSrc: string;
    bgColor: string;
    textColor: string;
  }
> = {
  [SportsEnum.Pickleball]: {
    label: 'Pickleball',
    iconSrc: '/images/programming/pickleball.png',
    bgColor: 'bg-brand-sea-blue-100',
    textColor: 'text-brand-blue-600',
  },
  [SportsEnum.Tennis]: {
    label: 'Tennis',
    iconSrc: '/images/programming/tennis.png',
    bgColor: 'bg-color-tennis-bg',
    textColor: 'text-color-tennis-text',
  },
  [SportsEnum.Padel]: {
    label: 'Padel',
    iconSrc: '/images/programming/padel.png',
    bgColor: 'bg-brand-sea-blue-100',
    textColor: 'text-brand-blue-600',
  },
  [SportsEnum.PlatformTennis]: {
    label: 'Platform Tennis',
    iconSrc: '/images/programming/pickleball.png',
    bgColor: 'bg-brand-sea-blue-100',
    textColor: 'text-brand-blue-600',
  },
};

/** The sport glyph SportBadge renders — for badges that pair a sport icon with other text. */
export const getSportIconSrc = (sport: SportsEnum) =>
  (sportConfig[sport] || sportConfig[SportsEnum.Pickleball]).iconSrc;

export default function SportBadge({
  sport,
  size = 'md',
  variant = 'tinted',
  detail,
  className,
  ...rest
}: SportBadgeProps) {
  const config = sportConfig[sport] || sportConfig[SportsEnum.Pickleball];
  const isNeutral = variant === 'neutral';

  return (
    <div
      className={classNames(
        'flex w-fit items-center justify-center gap-1 rounded-2xl',
        isNeutral
          ? 'border border-color-border-input-lightmode bg-color-bg-lightmode-primary text-color-text-lightmode-primary shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary dark:text-color-text-darkmode-primary'
          : classNames(config.bgColor, config.textColor),
        size === 'xs' && 'px-2 py-1 text-size-product-button-label-xs',
        size === 'sm' && 'px-3 py-1.5 text-size-product-button-label-small',
        size === 'md' && 'px-3 py-1.5 text-size-product-button-label-small',
        size === 'lg' && 'px-4 py-2 text-size-product-button-label-medium',
        className,
      )}
      {...rest}
    >
      <img
        src={config.iconSrc}
        alt={config.label}
        className={classNames(
          'shrink-0',
          size === 'xs' && 'h-3 w-3',
          size === 'sm' && 'h-4 w-4',
          size === 'md' && 'h-4 w-4',
          size === 'lg' && 'h-5 w-5',
        )}
      />
      <span className="shrink-0 font-medium leading-[1.2]">
        {config.label}
        {detail && (
          <>
            <span className="mx-1 text-color-text-brand">•</span>
            {detail}
          </>
        )}
      </span>
    </div>
  );
}
